/**
 * パートナー組織管理者向けプラン設定ページ
 *
 * - 上段: 当該パートナー (共通 + 独自) で利用可能なプラン一覧。
 *         独自プランは有効/無効をトグル切替可能。金額・付与等の変更はシステム管理側で行う。
 * - 下段: マネージドプラン (credit_plans.plan_code='managed') の説明文 HTML エディタ + プレビュー
 *
 * 保存時にバックエンドで bleach によるサニタイズを行い、
 * フロント側もプレビュー/表示時に DOMPurify で多層防御する。
 */

import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import DOMPurify, { type Config as DOMPurifyConfig } from 'dompurify';
import { Pencil, Save } from 'lucide-react';
import { apiClient } from '../../services/apiClient';
import './SiteSettingsPage.css';
import './OrgPlansPage.css';

type PlanCategory = 'personal' | 'business' | 'both';

const PLAN_CATEGORY_LABELS: Record<PlanCategory, string> = {
  personal: '個人向け',
  business: '法人向け',
  both: '両方に表示',
};

interface Plan {
  id: string;
  plan_code: string;
  display_name: string;
  monthly_fee: number;
  included_credits: number;
  max_users: number | null;
  max_storage_bytes: number | null;
  is_contact_sales: boolean;
  is_active: boolean;
  /** 個別契約用の非公開プラン。パートナー管理者は閲覧のみで編集不可 (API 側 403)。 */
  is_hidden: boolean;
  sort_order: number;
  partner_id: string | null;
  plan_category: PlanCategory;
  ads_api_unlimited: boolean;
}

interface ManagedPlanResponse {
  managed_plan_html: string;
  partner_id: string;
  plans: Plan[];
}

// フロント側プレビュー/表示用 DOMPurify 設定。サーバ側 bleach と同じ方針。
export const MANAGED_PLAN_PURIFY_CONFIG: DOMPurifyConfig = {
  ALLOWED_TAGS: [
    'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'small', 'span',
    'div', 'hr',
    'ul', 'ol', 'li',
    'h3', 'h4',
    'img',
  ],
  ALLOWED_ATTR: ['style', 'src', 'alt', 'width', 'height'],
  ALLOWED_URI_REGEXP: /^(?:https:|data:image\/)/i,
  FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'link', 'style'],
  FORBID_ATTR: ['class'],
};

function formatStorage(bytes: number | null): string {
  if (!bytes || bytes <= 0) return '—';
  const gb = bytes / (1024 * 1024 * 1024);
  if (gb >= 1) return `${gb % 1 === 0 ? gb.toFixed(0) : gb.toFixed(1)} GB`;
  const mb = bytes / (1024 * 1024);
  return `${Math.round(mb)} MB`;
}

export default function OrgPlansPage() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<ManagedPlanResponse>({
    queryKey: ['org-admin-managed-plan'],
    queryFn: async () => {
      const res = await apiClient.get<ManagedPlanResponse>('/org-admin/site-settings/managed-plan');
      return res.data;
    },
  });

  const [htmlDraft, setHtmlDraft] = useState('');

  useEffect(() => {
    if (data) setHtmlDraft(data.managed_plan_html ?? '');
  }, [data]);

  const saveHtmlMutation = useMutation({
    mutationFn: async (html: string) => {
      await apiClient.put('/org-admin/site-settings/managed-plan', {
        managed_plan_html: html,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-admin-managed-plan'] });
      queryClient.invalidateQueries({ queryKey: ['subscription-plans'] });
    },
  });

  const updatePlanMutation = useMutation({
    mutationFn: async ({
      planId,
      patch,
    }: {
      planId: string;
      patch: { display_name?: string; is_active?: boolean; plan_category?: PlanCategory };
    }) => {
      await apiClient.put(`/org-admin/site-settings/plans/${planId}`, patch);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-admin-managed-plan'] });
      queryClient.invalidateQueries({ queryKey: ['subscription-plans'] });
    },
  });

  // 独自プランの表示名ドラフト (編集モード中の値を保持し、blur/Enter で確定保存)
  const [nameDrafts, setNameDrafts] = useState<Record<string, string>>({});
  const [editingPlanId, setEditingPlanId] = useState<string | null>(null);
  useEffect(() => {
    if (!data?.plans) return;
    const initial: Record<string, string> = {};
    for (const p of data.plans) {
      if (p.partner_id && p.partner_id === data.partner_id) {
        initial[p.id] = p.display_name;
      }
    }
    setNameDrafts(initial);
  }, [data]);

  const startEditName = (plan: Plan) => {
    setNameDrafts((prev) => ({ ...prev, [plan.id]: plan.display_name }));
    setEditingPlanId(plan.id);
  };

  const commitName = (plan: Plan) => {
    const draft = (nameDrafts[plan.id] ?? '').trim();
    setEditingPlanId(null);
    if (!draft) {
      // 空は不可: 元の値に戻す
      setNameDrafts((prev) => ({ ...prev, [plan.id]: plan.display_name }));
      return;
    }
    if (draft === plan.display_name) return;
    updatePlanMutation.mutate({ planId: plan.id, patch: { display_name: draft } });
  };

  const cancelEditName = (plan: Plan) => {
    setNameDrafts((prev) => ({ ...prev, [plan.id]: plan.display_name }));
    setEditingPlanId(null);
  };

  const sanitizedPreview = DOMPurify.sanitize(htmlDraft, MANAGED_PLAN_PURIFY_CONFIG);

  const partnerId = data?.partner_id ?? null;
  const plans = data?.plans ?? [];
  const managedPlan = plans.find((p) => p.plan_code === 'managed');

  return (
    <div className="site-settings-page org-plans-page">
      <header className="site-settings-header">
        {/* ページタイトルは lm-header (OrgAdminLayout) 側に表示されるため、
            ここでの重複見出しは出さない (説明文のみ残す)。 */}
        <p className="site-settings-desc">
          パートナーで利用可能なプラン一覧と、マネージドプランの説明文を管理します。
        </p>
      </header>

      {/* プラン一覧 (read-only + 独自プランの有効化トグル) */}
      <section className="site-settings-section">
        <div className="site-settings-section-head">
          <h2>プラン一覧</h2>
        </div>
        <p className="site-settings-desc" style={{ marginTop: -4 }}>
          当該パートナー (および共通プラン) で利用可能なプラン。金額・ポイント付与等の設定変更はシステム管理側で行います。
          独自プランは有効/無効を切り替えできます。
        </p>
        {isLoading ? (
          <p className="site-settings-empty">読み込み中…</p>
        ) : plans.length === 0 ? (
          <p className="site-settings-empty">プランが見つかりません</p>
        ) : (
          <div className="org-plans-table-wrapper">
            <table className="org-plans-table">
              <thead>
                <tr>
                  <th>状態</th>
                  <th>プラン</th>
                  <th>月額</th>
                  <th>付与 pt/月</th>
                  <th>ユーザー数</th>
                  <th>最大容量</th>
                  <th>広告 API</th>
                  <th>課金</th>
                  <th>スコープ</th>
                  <th>カテゴリ</th>
                </tr>
              </thead>
              <tbody>
                {plans.map((p) => {
                  const isOwnPartnerPlan = !!p.partner_id && p.partner_id === partnerId;
                  // 非公開 (個別契約) プランは運営のみ編集可。パートナー管理者は閲覧のみ
                  // (バックエンドの PUT が 403 を返すため、UI 側も操作を無効化する)。
                  const isEditable = isOwnPartnerPlan && !p.is_hidden;
                  return (
                    <tr key={p.id}>
                      <td>
                        {isEditable ? (
                          <label
                            className="org-plans-toggle"
                            title={p.is_active ? '有効 (クリックで無効化)' : '無効 (クリックで有効化)'}
                          >
                            <input
                              type="checkbox"
                              checked={p.is_active}
                              disabled={updatePlanMutation.isPending}
                              onChange={(e) =>
                                updatePlanMutation.mutate({
                                  planId: p.id,
                                  patch: { is_active: e.target.checked },
                                })
                              }
                            />
                            <span className="org-plans-toggle-track" aria-hidden>
                              <span className="org-plans-toggle-thumb" />
                            </span>
                          </label>
                        ) : (
                          <label
                            className="org-plans-toggle"
                            title={p.is_active ? '有効' : '無効'}
                          >
                            <input type="checkbox" checked={p.is_active} disabled readOnly />
                            <span className="org-plans-toggle-track" aria-hidden>
                              <span className="org-plans-toggle-thumb" />
                            </span>
                          </label>
                        )}
                      </td>
                      <td>
                        {isEditable && editingPlanId === p.id ? (
                          <input
                            type="text"
                            className="org-plans-name-input"
                            value={nameDrafts[p.id] ?? p.display_name}
                            disabled={updatePlanMutation.isPending}
                            maxLength={100}
                            autoFocus
                            onChange={(e) =>
                              setNameDrafts((prev) => ({ ...prev, [p.id]: e.target.value }))
                            }
                            onBlur={() => commitName(p)}
                            onKeyDown={(e) => {
                              // IME 変換確定の Enter は無視 (isComposing / keyCode 229)
                              if (e.nativeEvent.isComposing || e.keyCode === 229) return;
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                commitName(p);
                              } else if (e.key === 'Escape') {
                                e.preventDefault();
                                cancelEditName(p);
                              }
                            }}
                          />
                        ) : (
                          <span className="org-plans-name-row">
                            <strong>{p.display_name}</strong>
                            {p.is_hidden && (
                              <span
                                className="org-plans-hidden-badge"
                                title="個別契約用の非公開プラン。設定変更は運営のみ可能です。"
                              >
                                非公開
                              </span>
                            )}
                            {isEditable && (
                              <button
                                type="button"
                                className="org-plans-name-edit-btn"
                                onClick={() => startEditName(p)}
                                title="プラン名を編集"
                                aria-label="プラン名を編集"
                              >
                                <Pencil size={12} strokeWidth={1.75} />
                              </button>
                            )}
                          </span>
                        )}
                        <div className="org-plans-table-code">{p.plan_code}</div>
                      </td>
                      <td>
                        {p.is_contact_sales && p.monthly_fee === 0
                          ? '—'
                          : `¥${Math.round(p.monthly_fee).toLocaleString()}`}
                      </td>
                      <td>{p.included_credits > 0 ? p.included_credits.toLocaleString() : '—'}</td>
                      <td>{p.max_users ?? '無制限'}</td>
                      <td>{formatStorage(p.max_storage_bytes)}</td>
                      <td>{p.ads_api_unlimited ? '無料' : '—'}</td>
                      <td>{p.is_contact_sales ? 'お問合せ' : '自動課金'}</td>
                      <td>{p.partner_id ? '独自' : '共通'}</td>
                      <td>
                        {isEditable ? (
                          <select
                            className="org-plans-category-select"
                            value={p.plan_category}
                            disabled={updatePlanMutation.isPending}
                            onChange={(e) =>
                              updatePlanMutation.mutate({
                                planId: p.id,
                                patch: { plan_category: e.target.value as PlanCategory },
                              })
                            }
                          >
                            <option value="personal">{PLAN_CATEGORY_LABELS.personal}</option>
                            <option value="business">{PLAN_CATEGORY_LABELS.business}</option>
                            <option value="both">{PLAN_CATEGORY_LABELS.both}</option>
                          </select>
                        ) : (
                          <span className="org-plans-status-readonly">
                            {PLAN_CATEGORY_LABELS[p.plan_category]}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* マネージドプラン HTML エディタ */}
      <section className="site-settings-section">
        <div className="site-settings-section-head">
          <h2>マネージドプランの説明文</h2>
          <button
            type="button"
            className="site-settings-upload-btn"
            onClick={() => saveHtmlMutation.mutate(htmlDraft)}
            disabled={saveHtmlMutation.isPending || !managedPlan}
          >
            <Save size={16} strokeWidth={1.75} />
            {saveHtmlMutation.isPending ? '保存中…' : '保存'}
          </button>
        </div>
        {!managedPlan && (
          <p className="site-settings-desc" style={{ color: 'var(--oem-danger)' }}>
            このパートナーには「マネージドプラン」(plan_code=managed) が設定されていません。プランをシステム管理側で有効化してください。
          </p>
        )}
        <p className="site-settings-desc" style={{ marginTop: -4 }}>
          契約・プランページのマネージドプランカードで表示される説明文を HTML で編集します。
          保存時にサーバ側で許容タグ/属性のみ残され、他は自動的に除去されます。
          空にするとデフォルトの表記が表示されます。
        </p>

        <details className="org-plans-allowed-help">
          <summary>使用できる HTML タグ・属性</summary>
          <ul>
            <li>
              テキスト装飾: <code>p</code> / <code>br</code> / <code>strong</code> / <code>b</code> / <code>em</code> / <code>i</code> / <code>u</code> / <code>s</code> / <code>small</code> / <code>span</code>
            </li>
            <li>構造: <code>div</code> / <code>hr</code></li>
            <li>リスト: <code>ul</code> / <code>ol</code> / <code>li</code></li>
            <li>見出し: <code>h3</code> / <code>h4</code></li>
            <li>
              画像: <code>&lt;img src="https://..." alt="..." width="..." height="..."&gt;</code>
              (<code>src</code> は https または data: の画像のみ)
            </li>
            <li>
              <code>style</code>: <code>color</code> / <code>background-color</code> / <code>text-align</code> / <code>font-weight</code> / <code>font-style</code> / <code>text-decoration</code> のみ
            </li>
          </ul>
          <p>
            禁止: <code>script</code> / <code>iframe</code> / <code>object</code> / <code>embed</code> / <code>link</code> / <code>style</code> タグ、<code>on*</code> イベント属性、<code>javascript:</code> URI、<code>class</code> 属性
          </p>
        </details>

        <div className="org-plans-editor-grid">
          <label className="site-settings-field">
            <span className="site-settings-field-label">HTML</span>
            <textarea
              className="site-settings-input site-settings-textarea org-plans-editor-textarea"
              value={htmlDraft}
              onChange={(e) => setHtmlDraft(e.target.value)}
              placeholder={'<p>要件と素材を送るだけ、運営チームが制作・納品します。</p>\n<ul>\n  <li>制作工数・学習コストゼロ</li>\n  <li>料金は内容に応じて個別見積</li>\n</ul>'}
              rows={14}
              spellCheck={false}
            />
          </label>
          <div className="site-settings-field">
            <span className="site-settings-field-label">プレビュー (サニタイズ後)</span>
            <div
              className="org-plans-preview"
              dangerouslySetInnerHTML={{
                __html:
                  sanitizedPreview ||
                  '<p style="color:var(--oem-text-soft)">プレビューはここに表示されます</p>',
              }}
            />
          </div>
        </div>
      </section>
    </div>
  );
}
